/**
 * NotificationManagerService - Central notification orchestration with user preference enforcement
 * 
 * Boundary: Does not send notifications directly; wraps ToastNotificationService and respects user settings
 * External dependencies: NotificationSettingsService (Firestore), UserService.user.uid
 */

import { Injectable, inject } from '@angular/core';
import { ToastNotificationService } from './toast-notification.service';
import { NotificationSettingsService } from './notification-settings.service';
import {
  NotificationType,
  UserNotificationSettings,
} from '@core/models/notification.types';
import { UserService } from './user.service';
import { LoggingService } from './logging.service';
import {
  EmailTemplateService,
  ScanCompletionTemplateData,
  SecurityAlertTemplateData,
  CriticalVulnerabilityTemplateData,
  SystemMaintenanceTemplateData,
} from './email-template-embedded.service';

/**
 * Orchestrates all user-facing notifications with preference filtering, quiet hours, and channel routing
 * 
 * Dependencies:
 * - ToastNotificationService: Toast display backend
 * - NotificationSettingsService: User preference persistence (Firestore)
 * - UserService: Current user context for loading preferences
 * - LoggingService: Error logging
 * - EmailTemplateService: Email template generation (templates not sent; TODO integration)
 * 
 * Side effects:
 * - Subscribes to UserService.getUserModel() on construction; loads/caches preferences on user change
 * - No Firestore writes (read-only preference checks)
 * - Subject updates: None (stateless beyond cached preferences)
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationManagerService {
  private toastNotificationService = inject(ToastNotificationService);
  private notificationSettingsService = inject(NotificationSettingsService);
  private userService = inject(UserService);
  private loggingService = inject(LoggingService);
  private emailTemplateService = inject(EmailTemplateService);

  private userSettings: UserNotificationSettings | null = null;
  private userId: string | null = null;

  constructor() {
    // Subscribe to user changes to keep settings updated
    this.userService.getUserModel().subscribe((user) => {
      if (user?.uid !== this.userId) {
        this.userId = user?.uid || null;
        this.loadUserSettings();
      }
    });
  }

  /**
   * Load user notification settings
   */
  private async loadUserSettings(): Promise<void> {
    if (!this.userId) {
      this.userSettings = null;
      return;
    }

    try {
      this.userSettings =
        await this.notificationSettingsService.getUserNotificationSettings(
          this.userId,
        );
    } catch (error) {
      this.loggingService.logError(
        'Failed to load user notification settings:',
        error,
      );
      this.userSettings = null;
    }
  }

  /**
   * Check if notifications are currently allowed based on user preferences
   */
  private shouldAllowNotification(
    notificationType?: NotificationType,
    channel: 'toast' | 'desktop' | 'email' | 'sms' = 'toast',
  ): boolean {
    // If no user settings loaded, allow notifications (graceful degradation)
    if (!this.userSettings) {
      return true;
    }

    // Check global notifications setting
    if (!this.userSettings.globalEnabled) {
      return false;
    }

    // Check quiet hours
    if (this.isInQuietHours()) {
      // Allow critical security alerts even during quiet hours
      if (notificationType === NotificationType.SECURITY_ALERTS) {
        return true;
      }

      return false;
    }

    // Check weekends-only setting
    if (this.userSettings.weekendsOnly && !this.isWeekend()) {
      // Allow critical notifications on weekdays
      if (
        notificationType === NotificationType.SECURITY_ALERTS ||
        notificationType === NotificationType.CRITICAL_VULNERABILITIES
      ) {
        return true;
      }

      return false;
    }

    // Check specific notification type preferences
    if (notificationType) {
      const preference = this.userSettings.preferences?.find(
        (p) => p.type === notificationType,
      );
      if (preference && !preference.enabled) {
        return false;
      }

      // Check channel-specific preferences
      if (preference) {
        switch (channel) {
          case 'email':
            return preference.email;
          case 'sms':
            return preference.sms;
          case 'toast':
          case 'desktop':
            return preference.push; // Use push setting for desktop/toast notifications
        }
      }
    }

    return true;
  }

  /**
   * Check if current time is within quiet hours
   */
  private isInQuietHours(): boolean {
    if (!this.userSettings?.quietHours?.enabled) {
      return false;
    }

    const now = new Date();
    const currentTime = this.formatTime(now);
    const startTime = this.userSettings.quietHours.startTime;
    const endTime = this.userSettings.quietHours.endTime;

    // Handle overnight quiet hours (e.g., 22:00 to 08:00)
    if (startTime > endTime) {
      return currentTime >= startTime || currentTime <= endTime;
    } else {
      // Same day quiet hours (e.g., 12:00 to 14:00)
      return currentTime >= startTime && currentTime <= endTime;
    }
  }

  /**
   * Check if current day is weekend
   */
  private isWeekend(): boolean {
    const now = new Date();
    const day = now.getDay();
    return day === 0 || day === 6; // Sunday = 0, Saturday = 6
  }

  /**
   * Format time as HH:MM string
   */
  private formatTime(date: Date): string {
    return date.toTimeString().slice(0, 5);
  }

  /**
   * Displays success toast notification if user preferences allow
   * 
   * @param title - Notification title
   * @param message - Notification body text
   * @param duration - Optional display duration in ms (default varies by ToastNotificationService)
   * @param action - Optional action button config
   * @param notificationType - Optional type for preference filtering (e.g., NotificationType.SCAN_COMPLETION)
   * @returns Toast ID string if shown, null if suppressed by preferences
   * - Observable semantics: N/A (synchronous delegation to toast service)
   * - HTTP errors: N/A
   * - State touched: Delegates to ToastNotificationService if allowed; no persistence
   * - Idempotency: Safe to call multiple times; creates new toasts each time
   */
  showSuccess(
    title: string,
    message: string,
    duration?: number,
    action?: { label: string; callback: () => void },
    notificationType?: NotificationType,
  ): string | null {
    if (!this.shouldAllowNotification(notificationType, 'toast')) {
      return null;
    }

    return this.toastNotificationService.showSuccess(
      title,
      message,
      duration,
      action,
    );
  }

  /**
   * Displays an error toast.
   *
   * Two kinds of error arrive here, told apart by `notificationType`:
   *
   * - **Action failures** (no `notificationType`): the direct result of
   *   something the user just did — Launch Scan, Register Scanner, Save
   *   Credential. There is no preference category for these, so the user has
   *   never been able to opt out of them, and hiding one leaves them looking
   *   at a re-enabled button with no idea why nothing happened (#764). They
   *   are always shown: global disable, quiet hours, weekends-only and
   *   unloaded preferences are all ignored (fail open).
   * - **Event notifications** (a `notificationType` is given): background
   *   alerts such as `FAILED_SCANS` or `CRITICAL_VULNERABILITIES` that the
   *   user tunes from the notification-settings page. These keep honouring
   *   preferences; a suppressed one is still logged. If the preference check
   *   itself throws, the error is shown rather than lost.
   *
   * @param title - Notification title
   * @param message - Notification body text
   * @param duration - Optional display duration in ms (0 = persist until dismissed)
   * @param action - Optional action button config
   * @param notificationType - Event category for preference filtering; omit for action failures
   * @returns Toast ID string if shown, null if a typed event was suppressed by preferences
   * - Observable semantics: N/A (synchronous delegation to toast service)
   * - HTTP errors: N/A
   * - State touched: Logs suppressed typed errors via LoggingService; delegates to ToastNotificationService
   * - Idempotency: Safe to call multiple times; creates new toasts each time
   *
   * @example
   * // Preference-filtered — a background failed-scan digest:
   * notificationManager.showError('Scan Failed', msg, 0, retry, NotificationType.FAILED_SCANS);
   * // Preference-filtered — a page-init load that failed on its own:
   * notificationManager.showError('Error', 'Failed to load company information');
   * // For an error the user just caused, use showActionError instead.
   */
  showError(
    title: string,
    message: string,
    duration?: number,
    action?: { label: string; callback: () => void },
    notificationType?: NotificationType,
  ): string | null {
    if (!this.isTypedErrorAllowed(notificationType)) {
      // Still record it: suppressing an event notification is the user's
      // choice, but the error itself must not vanish from telemetry.
      this.loggingService.logError(
        `Critical error (notification suppressed): ${title} - ${message}`,
      );
      return null;
    }

    return this.toastNotificationService.showError(
      title,
      message,
      duration,
      action,
    );
  }

  /**
   * Displays an error toast for something the user just did, bypassing
   * notification preferences.
   *
   * Intent is explicit here rather than inferred from an omitted
   * `notificationType`. An absent type does not mean "the user did this": plenty
   * of background work calls the untyped `showError` overload — page
   * initialisation in `NinjaScannerCreateComponent.ngOnInit()` via
   * `loadCompanyInfo()`, and the scanner list — and silently forcing those past
   * quiet hours and a disabled-notifications setting would break the preference
   * contract the user agreed to.
   *
   * Use this ONLY when the toast is the direct, synchronous answer to an action
   * the user took (submitting a form, clicking Launch). A user who presses a
   * button is owed the outcome; a background refresh is not an event they asked
   * to be interrupted for. Everything else belongs in `showError`.
   *
   * @param title - Notification title
   * @param message - Notification body text
   * @param duration - Optional display duration in ms
   * @param action - Optional action button config
   * @returns Toast ID string (never null — this path is not suppressible)
   * - Observable semantics: N/A (synchronous delegation to toast service)
   * - HTTP errors: N/A
   * - State touched: Delegates to ToastNotificationService
   * - Idempotency: Safe to call multiple times; creates new toasts each time
   */
  showActionError(
    title: string,
    message: string,
    duration?: number,
    action?: { label: string; callback: () => void },
  ): string {
    return this.toastNotificationService.showError(
      title,
      message,
      duration,
      action,
    );
  }

  /**
   * Preference check for typed (event) errors that fails OPEN.
   *
   * A malformed preference document must never hide an error, so any
   * exception from the check is logged and treated as "allowed".
   */
  private isTypedErrorAllowed(notificationType?: NotificationType): boolean {
    try {
      return this.shouldAllowNotification(notificationType, 'toast');
    } catch (error) {
      this.loggingService.logError(
        'Notification preference check failed; showing error toast anyway',
        error,
      );
      return true;
    }
  }

  /**
   * Displays warning toast notification if user preferences allow
   * 
   * @param title - Notification title
   * @param message - Notification body text
   * @param duration - Optional display duration in ms
   * @param action - Optional action button config
   * @param notificationType - Optional type for preference filtering
   * @returns Toast ID string if shown, null if suppressed by preferences
   * - Observable semantics: N/A (synchronous delegation to toast service)
   * - HTTP errors: N/A
   * - State touched: Delegates to ToastNotificationService if allowed
   * - Idempotency: Safe to call multiple times; creates new toasts each time
   */
  showWarning(
    title: string,
    message: string,
    duration?: number,
    action?: { label: string; callback: () => void },
    notificationType?: NotificationType,
  ): string | null {
    if (!this.shouldAllowNotification(notificationType, 'toast')) {
      return null;
    }

    return this.toastNotificationService.showWarning(
      title,
      message,
      duration,
      action,
    );
  }

  /**
   * Displays info toast notification if user preferences allow
   * 
   * @param title - Notification title
   * @param message - Notification body text
   * @param duration - Optional display duration in ms
   * @param action - Optional action button config
   * @param notificationType - Optional type for preference filtering
   * @returns Toast ID string if shown, null if suppressed by preferences
   * - Observable semantics: N/A (synchronous delegation to toast service)
   * - HTTP errors: N/A
   * - State touched: Delegates to ToastNotificationService if allowed
   * - Idempotency: Safe to call multiple times; creates new toasts each time
   */
  showInfo(
    title: string,
    message: string,
    duration?: number,
    action?: { label: string; callback: () => void },
    notificationType?: NotificationType,
  ): string | null {
    if (!this.shouldAllowNotification(notificationType, 'toast')) {
      return null;
    }

    return this.toastNotificationService.showInfo(
      title,
      message,
      duration,
      action,
    );
  }

  /**
   * Displays scan completion notification with optional action button
   * 
   * @param scanName - Human-readable scan identifier
   * @param resultSummary - Optional summary text (e.g., "3 vulnerabilities found")
   * @returns Toast ID string if shown, null if suppressed by preferences
   * - Observable semantics: N/A (synchronous delegation)
   * - HTTP errors: N/A
   * - State touched: None; action callback is placeholder (TODO: wire to router navigation)
   * - Idempotency: Safe to call multiple times
   */
  showScanCompletion(scanName: string, resultSummary?: string): string | null {
    const title = 'Scan Completed';
    const message = resultSummary
      ? `${scanName} scan completed. ${resultSummary}`
      : `${scanName} scan has completed successfully.`;

    return this.showSuccess(
      title,
      message,
      undefined,
      {
        label: 'View Results',
        callback: () => {
          // Navigate to scan results - this could be enhanced with routing
        },
      },
      NotificationType.SCAN_COMPLETION,
    );
  }

  /**
   * Displays scan failure notification with retry action button
   * 
   * @param scanName - Human-readable scan identifier
   * @param errorDetails - Optional error message or code
   * @returns Toast ID string if shown, null if suppressed by preferences
   * - Observable semantics: N/A (synchronous delegation)
   * - HTTP errors: N/A
   * - State touched: None; action callback is placeholder (TODO: wire to scan retry logic)
   * - Idempotency: Safe to call multiple times
   */
  showScanFailure(scanName: string, errorDetails?: string): string | null {
    const title = 'Scan Failed';
    const message = errorDetails
      ? `${scanName} scan failed: ${errorDetails}`
      : `${scanName} scan failed to complete.`;

    return this.showError(
      title,
      message,
      0,
      {
        label: 'Retry Scan',
        callback: () => {},
      },
      NotificationType.FAILED_SCANS,
    );
  }

  /**
   * Displays critical vulnerability alert with details action button
   * 
   * @param vulnerability - Vulnerability name or identifier
   * @param count - Optional count of affected assets
   * @returns Toast ID string if shown, null if suppressed by preferences
   * - Observable semantics: N/A (synchronous delegation)
   * - HTTP errors: N/A
   * - State touched: None; action callback is placeholder (TODO: wire to vulnerability details view)
   * - Idempotency: Safe to call multiple times
   */
  showCriticalVulnerability(
    vulnerability: string,
    count?: number,
  ): string | null {
    const title = 'Critical Vulnerability Detected';
    const message = count
      ? `${count} critical vulnerabilities found including: ${vulnerability}`
      : `Critical vulnerability detected: ${vulnerability}`;

    return this.showError(
      title,
      message,
      0,
      {
        label: 'View Details',
        callback: () => {},
      },
      NotificationType.CRITICAL_VULNERABILITIES,
    );
  }

  /**
   * Displays security alert notification (always shown unless globally disabled)
   * 
   * @param alertMessage - Alert description
   * @param severity - Alert severity level (critical, high, medium; default 'high')
   * @returns Toast ID string if shown, null if suppressed by global setting
   * - Observable semantics: N/A (synchronous delegation)
   * - HTTP errors: N/A
   * - State touched: Logs suppressed critical alerts via LoggingService even if globally disabled
   * - Idempotency: Safe to call multiple times
   * 
   * @example
   * // Security alerts bypass quiet hours and weekends-only preferences
   */
  showSecurityAlert(
    alertMessage: string,
    severity: 'critical' | 'high' | 'medium' = 'high',
  ): string | null {
    const title = `Security Alert (${severity.toUpperCase()})`;

    // Security alerts are always shown regardless of preferences (except if globally disabled)
    if (!this.userSettings?.globalEnabled) {
      // Even if globally disabled, log critical security alerts
      this.loggingService.logError(
        `SECURITY ALERT (notification suppressed): ${alertMessage}`,
      );
      return null;
    }

    return this.toastNotificationService.showError(title, alertMessage, 0, {
      label: 'Investigate',
      callback: () => {},
    });
  }

  /**
   * Displays system maintenance notification
   * 
   * @param maintenanceDetails - Maintenance description
   * @param startTime - Optional scheduled start time
   * @returns Toast ID string if shown, null if suppressed by preferences
   * - Observable semantics: N/A (synchronous delegation)
   * - HTTP errors: N/A
   * - State touched: None
   * - Idempotency: Safe to call multiple times
   */
  showSystemMaintenance(
    maintenanceDetails: string,
    startTime?: Date,
  ): string | null {
    const title = 'System Maintenance';
    const message = startTime
      ? `${maintenanceDetails} Scheduled for ${startTime.toLocaleString()}`
      : maintenanceDetails;

    return this.showInfo(
      title,
      message,
      undefined,
      undefined,
      NotificationType.SYSTEM_MAINTENANCE,
    );
  }

  /**
   * Displays compliance alert notification with review action button
   * 
   * @param complianceIssue - Issue description
   * @param deadline - Optional compliance deadline
   * @returns Toast ID string if shown, null if suppressed by preferences
   * - Observable semantics: N/A (synchronous delegation)
   * - HTTP errors: N/A
   * - State touched: None; action callback is placeholder (TODO: wire to compliance review)
   * - Idempotency: Safe to call multiple times
   */
  showComplianceAlert(complianceIssue: string, deadline?: Date): string | null {
    const title = 'Compliance Alert';
    const message = deadline
      ? `${complianceIssue} Deadline: ${deadline.toLocaleDateString()}`
      : complianceIssue;

    return this.showWarning(
      title,
      message,
      undefined,
      {
        label: 'Review',
        callback: () => {},
      },
      NotificationType.COMPLIANCE_ALERTS,
    );
  }

  /**
   * Retrieves current notification preference status snapshot
   * 
   * @returns Object with global enabled, quiet hours status, weekends-only setting, current time, and quiet hours range
   * - Observable semantics: N/A (synchronous)
   * - HTTP errors: N/A
   * - State touched: None (reads cached userSettings)
   * - Idempotency: Safe read-only operation
   */
  getNotificationStatus(): {
    globalEnabled: boolean;
    inQuietHours: boolean;
    weekendsOnly: boolean;
    currentTime: string;
    quietHoursRange?: string;
  } {
    return {
      globalEnabled: this.userSettings?.globalEnabled ?? true,
      inQuietHours: this.isInQuietHours(),
      weekendsOnly: this.userSettings?.weekendsOnly ?? false,
      currentTime: this.formatTime(new Date()),
      quietHoursRange: this.userSettings?.quietHours?.enabled
        ? `${this.userSettings.quietHours.startTime} - ${this.userSettings.quietHours.endTime}`
        : undefined,
    };
  }

  /**
   * Forces reload of user notification settings from Firestore
   * 
   * @returns Promise resolving when settings refreshed
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; sets userSettings to null on failure
   * - State touched: Reads Firestore via NotificationSettingsService; updates cached userSettings
   * - Idempotency: Safe to call multiple times; always fetches latest from Firestore
   * 
   * @example
   * // Call after user updates preferences in settings UI
   */
  async refreshUserSettings(): Promise<void> {
    await this.loadUserSettings();
  }

  /**
   * Checks if specific notification type is enabled for the current user
   * 
   * @param notificationType - Notification type enum value
   * @returns true if enabled (or if settings not loaded), false if explicitly disabled
   * - Observable semantics: N/A (synchronous)
   * - HTTP errors: N/A
   * - State touched: None (reads cached userSettings)
   * - Idempotency: Safe read-only operation
   */
  isNotificationTypeEnabled(notificationType: NotificationType): boolean {
    if (!this.userSettings) return true;

    const preference = this.userSettings.preferences?.find(
      (p) => p.type === notificationType,
    );
    return preference?.enabled ?? true;
  }

  /**
   * Retrieves raw user notification settings (for debugging or advanced usage)
   * 
   * @returns Current cached settings or null if not loaded
   * - Observable semantics: N/A (synchronous)
   * - HTTP errors: N/A
   * - State touched: None (reads cached userSettings)
   * - Idempotency: Safe read-only operation
   */
  getCurrentSettings(): UserNotificationSettings | null {
    return this.userSettings;
  }

  // =============================================================================
  // EMAIL NOTIFICATION METHODS
  // =============================================================================

  /**
   * Sends scan completion email notification if user preferences allow
   * 
   * @param userEmail - Recipient email address
   * @param data - Scan completion template data
   * @returns Promise<boolean> true on successful send, false if suppressed by preferences or error
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: None (template generated but not sent; TODO: integrate with email service)
   * - Idempotency: Safe to call multiple times; TODO implementation may send duplicate emails
   * 
   * @example
   * // TODO: Replace template generation with actual email sending service (SendGrid, SES, etc.)
   */
  async sendScanCompletionEmail(
    userEmail: string,
    data: ScanCompletionTemplateData,
  ): Promise<boolean> {
    if (
      !this.shouldAllowNotification(NotificationType.SCAN_COMPLETION, 'email')
    ) {
      return false;
    }

    try {
      const _template =
        this.emailTemplateService.getScanCompletionTemplate(data);

      // Here you would integrate with your email service (SendGrid, SES, etc.)
      // For now, we'll log the email content
      // TODO: Replace with actual email sending service

      return true;
    } catch (error) {
      this.loggingService.logError(
        'Failed to send scan completion email:',
        error,
      );
      return false;
    }
  }
  /**
   * Sends security alert email notification if user preferences allow
   * 
   * @param userEmail - Recipient email address
   * @param data - Security alert template data
   * @returns Promise<boolean> true on successful send, false if suppressed by preferences or error
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: None (template generated but not sent; TODO: integrate with email service)
   * - Idempotency: Safe to call multiple times; TODO implementation may send duplicate emails
   */
  async sendSecurityAlertEmail(
    userEmail: string,
    data: SecurityAlertTemplateData,
  ): Promise<boolean> {
    if (
      !this.shouldAllowNotification(NotificationType.SECURITY_ALERTS, 'email')
    ) {
      return false;
    }

    try {
      const _template =
        this.emailTemplateService.getSecurityAlertTemplate(data);

      // TODO: Replace with actual email sending service

      return true;
    } catch (error) {
      this.loggingService.logError(
        'Failed to send security alert email:',
        error,
      );
      return false;
    }
  }

  /**
   * Sends critical vulnerability email notification if user preferences allow
   * 
   * @param userEmail - Recipient email address
   * @param data - Critical vulnerability template data
   * @returns Promise<boolean> true on successful send, false if suppressed by preferences or error
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: None (template generated but not sent; TODO: integrate with email service)
   * - Idempotency: Safe to call multiple times; TODO implementation may send duplicate emails
   */
  async sendCriticalVulnerabilityEmail(
    userEmail: string,
    data: CriticalVulnerabilityTemplateData,
  ): Promise<boolean> {
    if (
      !this.shouldAllowNotification(
        NotificationType.CRITICAL_VULNERABILITIES,
        'email',
      )
    ) {
      return false;
    }

    try {
      const _template =
        this.emailTemplateService.getCriticalVulnerabilityTemplate(data);

      // TODO: Replace with actual email sending service

      return true;
    } catch (error) {
      this.loggingService.logError(
        'Failed to send critical vulnerability email:',
        error,
      );
      return false;
    }
  }

  /**
   * Sends system maintenance email notification if user preferences allow
   * 
   * @param userEmail - Recipient email address
   * @param data - System maintenance template data
   * @returns Promise<boolean> true on successful send, false if suppressed by preferences or error
   * - Observable semantics: N/A (Promise-based async operation)
   * - HTTP errors: Logged via LoggingService; returns false on error
   * - State touched: None (template generated but not sent; TODO: integrate with email service)
   * - Idempotency: Safe to call multiple times; TODO implementation may send duplicate emails
   */
  async sendSystemMaintenanceEmail(
    userEmail: string,
    data: SystemMaintenanceTemplateData,
  ): Promise<boolean> {
    if (
      !this.shouldAllowNotification(
        NotificationType.SYSTEM_MAINTENANCE,
        'email',
      )
    ) {
      return false;
    }

    try {
      const _template =
        this.emailTemplateService.getSystemMaintenanceTemplate(data);

      // TODO: Replace with actual email sending service EmailTemplateService

      return true;
    } catch (error) {
      this.loggingService.logError(
        'Failed to send system maintenance email:',
        error,
      );
      return false;
    }
  }

  /**
   * Sends batch emails to multiple recipients
   * 
   * @param emailType - Type of email template to use
   * @param recipients - Array of recipient email addresses
   * @param templateData - Shared template data for all recipients
   * @returns Promise with sent/failed counts and per-recipient results array
   * - Observable semantics: N/A (Promise-based async operation; sequential sends)
   * - HTTP errors: Individual recipient errors logged; aggregated in result object
   * - State touched: None (templates generated but not sent; TODO: integrate with email service)
   * - Idempotency: Safe to call multiple times; TODO implementation may send duplicate emails
   * 
   * @example
   * // Useful for alerts affecting multiple users (e.g., system-wide security alert)
   */
  async sendBatchEmails(
    emailType:
      | 'scan-completion'
      | 'security-alert'
      | 'critical-vulnerability'
      | 'system-maintenance',
    recipients: string[],
    templateData:
      | ScanCompletionTemplateData
      | SecurityAlertTemplateData
      | CriticalVulnerabilityTemplateData
      | SystemMaintenanceTemplateData,
  ): Promise<{ sent: number; failed: number; results: boolean[] }> {
    const results: boolean[] = [];
    let sent = 0;
    let failed = 0;

    for (const email of recipients) {
      let result = false;

      try {
        switch (emailType) {
          case 'scan-completion':
            result = await this.sendScanCompletionEmail(
              email,
              templateData as ScanCompletionTemplateData,
            );
            break;
          case 'security-alert':
            result = await this.sendSecurityAlertEmail(
              email,
              templateData as SecurityAlertTemplateData,
            );
            break;
          case 'critical-vulnerability':
            result = await this.sendCriticalVulnerabilityEmail(
              email,
              templateData as CriticalVulnerabilityTemplateData,
            );
            break;
          case 'system-maintenance':
            result = await this.sendSystemMaintenanceEmail(
              email,
              templateData as SystemMaintenanceTemplateData,
            );
            break;
        }

        if (result) {
          sent++;
        } else {
          failed++;
        }
      } catch (error) {
        this.loggingService.logError(
          `Failed to send ${emailType} email to ${email}:`,
          error,
        );
        failed++;
      }

      results.push(result);
    }

    return { sent, failed, results };
  }

  /**
   * Generates and logs sample critical vulnerability email template (testing/debugging only)
   * 
   * @returns void
   * - Observable semantics: N/A (synchronous operation)
   * - HTTP errors: N/A
   * - State touched: Console output only; logs template to console and LoggingService on error
   * - Idempotency: Safe to call multiple times; generates same sample template each time
   */
  testEmailTemplateRendering(): void {
    const sampleData: CriticalVulnerabilityTemplateData = {
      vulnerabilityName: 'SQL Injection Vulnerability',
      cveId: 'CVE-2025-DEMO-002',
      cvssScore: 8.1,
      vulnerabilityDescription:
        'High severity SQL injection vulnerability allows remote code execution through database queries.',
      affectedAssetsCount: 3,
      riskLevel: 'Critical',
      exploitability: 'High',
      affectedAssets: [
        {
          name: 'demo-server-01',
          ipAddress: '10.0.1.100',
          service: 'MySQL',
          status: 'Vulnerable',
        },
        {
          name: 'demo-server-02',
          ipAddress: '10.0.1.101',
          service: 'PostgreSQL',
          status: 'Vulnerable',
        },
      ],
      vulnDetailsUrl: '/vulnerabilities/CVE-2025-DEMO-002',
      remediationUrl: '/remediation/CVE-2025-DEMO-002',
      vulnerabilityUrl: '/vulnerabilities',
      logoUrl:
        'https://cdn.prod.website-files.com/67368f6c79297610e225f43b/67378b875c07c4139458e3ba_logo-top.png',
      dashboardUrl: '/dashboard',
      settingsUrl: '/settings',
      supportUrl: '/support',
      unsubscribeUrl: '/unsubscribe',
    };

    try {
      const _template =
        this.emailTemplateService.getCriticalVulnerabilityTemplate(sampleData);
    } catch (error) {
      console.error('❌ Email template test failed:', error);
      this.loggingService.logError('Email template test failed:', error);
    }
  }
}
